import {partition} from '@benchling/js-utils';
import React from 'react';

import isArchived from 'archive/isArchived';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import {Storable, StorableSummary} from 'constants/SampleConstants';
import {PotentialChildStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ConnectedParentStorableSelectTree} from 'samples/storables/ParentStorableSelectTree';
import {storableToSelectTreeItem} from 'samples/storables/ParentStorableUtil';
import StorableStore from 'samples/StorableStore';
import * as SelectTreeConstants from 'ui-library/select-tree/SelectTreeConstants';
import APIUtil from 'util/APIUtil';

import {StorableSummaryGraphQL} from '../../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

function getIdsFromStorables(storables: Array<StorableSummary>): Array<string> {
  return storables.map((storable: Storable) => storable.id);
}

export interface LocationSearchDropdownTreeProps {
  results: Array<StorableSummary>;
  registryId: string;
  storableTypes: Array<StorableItemType>;
  onSetData: (typedValue: TypedValue | null) => void;
  // When presenting search results, LocationSearchDropdownTree is responsible for supplying the search
  // results to ConnectedParentStorableSelectTree. Otherwise ConnectedParentStorableSelectTree fetches the
  // data to show the full location hierarchy.
  isSearchResults: boolean;
  includeArchived: boolean;
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
  storableSchemaId?: string;
}

interface EnhancedProps {
  treeLocations: Array<SelectTreeConstants.TreeItem>;
  treeLeaves: Array<SelectTreeConstants.TreeItem>;
}

class LocationSearchDropdownTreeConnector extends DataConnector<
  LocationSearchDropdownTreeProps,
  EnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [StorableStore];

  override forceFetchOnMount(): void {
    if (this.props.results.length) {
      StorableSummaryExtrasManager.fetchMulti(getIdsFromStorables(this.props.results));
    }
  }

  enhanceProps(): EnhancedProps {
    // Don't fetch if showing search results only since ConnectedParentStorableSelectTree does fetching.
    if (!this.props.isSearchResults) {
      return {treeLocations: [], treeLeaves: []};
    }

    // StorableSummaryExtras are fetched above in forceFetchOnMount().
    const ancestralStorableIdsByStorableId =
      this.storableStore().getStorableSummaryExtras().ancestralStorableIdsByStorableId;
    if (
      !ancestralStorableIdsByStorableId ||
      this.props.results.some((storable) => !ancestralStorableIdsByStorableId[storable.id])
    ) {
      return this.fallBackToLoading();
    }

    // We want to remove duplicates so that the tree only displays the same item once.
    const treeItemIds: Array<string> = Array.from(
      new Set(this.props.results.flatMap((result) => ancestralStorableIdsByStorableId[result.id]))
    );
    const treeItemSummaries = Object.values(
      this.getOrFetchMultipleRequired(ManagedModelType.STORABLE_SUMMARY, treeItemIds)
    );
    // Filter out archived locations if specified.
    const filteredTreeItemSummaries = treeItemSummaries.filter(
      (treeLeafSummary) => !isArchived(treeLeafSummary) || this.props.includeArchived
    );
    const [treeLeafSummaries, treeLocationSummaries] = partition(
      filteredTreeItemSummaries,
      (summary) => APIUtil.apiIdToType(summary.id) !== ModelTypeConstants.LOCATION
    );

    // Get space available info for grid storables
    const childContainerStatusesByGridId =
      this.storableStore().getStorableSummaryExtras().childContainerStatusesByGridId;
    if (
      !childContainerStatusesByGridId ||
      treeLeafSummaries.some((summary) => !childContainerStatusesByGridId[summary.id])
    ) {
      return this.fallBackToLoading();
    }

    return {
      treeLocations: treeLocationSummaries.map((summary) =>
        storableToSelectTreeItem({
          storable: summary,
          isInitiallyExpanded: true,
          extras: this.storableStore().getStorableSummaryExtras(),
          storablesToBeMoved: this.props.storablesToBeMoved,
          parentStorableTypes: this.props.storableTypes,
        })
      ),
      treeLeaves: treeLeafSummaries.map((summary) =>
        storableToSelectTreeItem({
          storable: summary,
          isInitiallyExpanded: false,
          extras: this.storableStore().getStorableSummaryExtras(),
          storablesToBeMoved: this.props.storablesToBeMoved,
          parentStorableTypes: this.props.storableTypes,
        })
      ),
    };
  }
}

/**
 * Displays a select tree with storables in their expanded direct ancestries.
 */
class LocationSearchDropdownTree extends React.Component<LocationSearchDropdownTreeProps & EnhancedProps> {
  private handleSelectStorable = (item: StorableSummaryGraphQL | null): void => {
    if (item) {
      const storableInfo = {id: item.id, name: item.name, barcode: item.barcode?.value};
      const storableTypedValue: TypedValue = {type: 'STORABLES', storableSummaries: [storableInfo]};
      this.props.onSetData(storableTypedValue);
    }
  };

  override render(): JSX.Element {
    return (
      <ConnectedParentStorableSelectTree
        onSelectStorable={this.handleSelectStorable}
        registryId={this.props.registryId}
        selectedStorable={null}
        storableTypes={this.props.storableTypes}
        borderless={true}
        includeArchived={this.props.includeArchived}
        storablesToBeMoved={this.props.storablesToBeMoved}
        storableSchemaId={this.props.storableSchemaId}
        {...(this.props.isSearchResults
          ? {
              selectTreeItems: this.props.treeLocations,
              baseLeafItems: this.props.treeLeaves,
              shouldNotLoadMoreOnMount: true,
            }
          : {})}
      />
    );
  }
}

export default LocationSearchDropdownTreeConnector.connect(LocationSearchDropdownTree);
